import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { Close, ArrowUpward, ArrowDownward, Search, AddCircleOutlineRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItemButton,
  Stack,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import {
  baseApi,
  flownodesApi,
  processesApi,
  organizationsApi,
  tagsApi,
} from "api";
import dayjs from "dayjs";
import { getTranslation } from "common";
import { getDefaultValueForType, getUserSchema, toBase64 } from "common/utilities";
import { PrioritiesDropDown } from "./PrioritiesDropDown";
import {
  useBreakpoint,
  useForm,
  useSelectedOrganization,
  useSnackbar,
  useTranslations,
} from "hooks";
import { isRequired, Validation } from "hooks/useForm";
import { InputComment, FieldsList } from "components";
import { Priority } from "hooks/usePriorities";
import DropZone from "../DropZone";
import noModel from "../../image/empty-pana.svg";
import { useNavigate } from "react-router-dom";
import LabelManager from "../labels/LabelManager";
import { LabelData } from "components/labels/label_Interface";
import Labels from "components/labels/Labels";
import { SortState } from "hooks/useSortableList";
import { UserData } from "hooks/useUserDataKeys";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";

const INITIAL_STATE = {
  name: "",
  meta: {},
  comment: "",
  selectedProcess: null,
};
interface Process {
  assignmentmode: number;
  id: number;
  idorganization: number;
  idprocessmodel: number;
  idswimlane: number;
  idtype: number;
  name: string;
  processmodelname: string;
  swimlanename: string;
  userschema: string;
}

interface StartProcessProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isAdmin?: boolean;
}

const StartProcess: React.FC<StartProcessProps> = ({
  open,
  setOpen,
  isAdmin = false,
  // refresh,
}) => {
  const selectedOrganization = useSelectedOrganization();
  const [processes, setProcesses] = useState<Process[]>([]);
  const [selectedProcess, setSelectedProcess] = useState<Process | null>(null);
  const [allPriorities, setAllPriorities] = useState<Priority[]>([]);
  const [checked, setChecked] = useState<LabelData[]>([]);
  const [selectedPriority, setSelectedPriority] = useState<Priority | null>(
    null
  );
  const [openAddFieldDialog, setOpenAddFieldDialog] = useState(false);
  const administrator = true;

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const handleFileChange = (files: File[] | null) => {
    if (files) {
      setSelectedFiles(files);
    }
  };

  const [isLabelManagerShown, setIsLabelManagerShown] =
    useState<HTMLElement | null>(null);
  const handleLabelManagerClose = () => {
    setIsLabelManagerShown(null);
  };

  const { sendSnack } = useSnackbar();
  const translations = useTranslations();
  const validations: Validation[] = [
    ({ name }) =>
      isRequired(name) || {
        name: getTranslation(translations, "register.fieldrequired"),
      },
  ];

  const { changeHandler, reset, values, errors, touched, isValid } = useForm(
    INITIAL_STATE,
    validations
  );
  const navigate = useNavigate();
  const isMobile = useBreakpoint("down", "sm");

  // Aggiungi refs per la lista e gli elementi
  const listRef = useRef<HTMLUListElement>(null);
  const itemRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  // Funzione per scrollare al processo selezionato
  const scrollToSelectedProcess = useCallback(() => {
    if (selectedProcess && itemRefs.current[selectedProcess.id]) {
      const element = itemRefs.current[selectedProcess.id];
      if (element) {
        // Forza lo scroll immediato
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }
    }
  }, [selectedProcess]);

  useEffect(() => {
    if (open && selectedProcess?.userschema) {
      const schema = getUserSchema(selectedProcess.userschema);
      const newMeta: { [key: string]: UserData } = {};
      for (const item of schema) {
        newMeta[item.name] = {
          inCard: item.inCard,
          type: item.type,
          value: String(getDefaultValueForType(item.type))
        };
      }
      changeHandler("meta", newMeta);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProcess?.userschema, open]);

  const [loading, setLoading] = useState<boolean>(false);
  const [sortOrder, setSortOrder] = useState<SortState>(SortState.Asc);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const sortProcessesAsc = () => {
    const sorted = [...processes].sort((a, b) =>
      a.processmodelname.localeCompare(b.processmodelname)
    );
    setProcesses(sorted);
    setSortOrder(SortState.Asc);
  };

  const sortProcessesDesc = () => {
    const sorted = [...processes].sort((a, b) =>
      b.processmodelname.localeCompare(a.processmodelname)
    );
    setProcesses(sorted);
    setSortOrder(SortState.Desc);
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await baseApi.get(
          `startevents/organizations/${selectedOrganization?.id}`
        );
        const sortedProcesses = res.data.possiblestart.sort(
          (a: { processmodelname: string }, b: { processmodelname: any }) =>
            a.processmodelname.localeCompare(b.processmodelname)
        );
        setProcesses(sortedProcesses);
      } catch {
      } finally {
      }
    })();
  }, [selectedOrganization]);

  useEffect(() => {
    if (!open) {
      setChecked([]);
      reset();
      setSelectedFiles([]);
      setSearchQuery(""); // Reset search quando il dialogo si chiude
    }
  }, [processes, open, reset]);

  // Filtra i processi in base alla ricerca
  const filteredProcesses = useMemo(() => {
    if (!searchQuery.trim()) {
      return processes;
    }

    const query = searchQuery.toLowerCase();
    return processes.filter((process) => {
      const processName = process.name === ""
        ? process.processmodelname
        : `${process.processmodelname} (${process.name})`;
      return processName.toLowerCase().includes(query);
    });
  }, [processes, searchQuery]);

  const fetchOrganizationPriorities = useCallback(async () => {
    if (!selectedOrganization?.id) {
      return;
    }

    const response = await organizationsApi.get(
      `/${selectedOrganization?.id}/priorities`
    );
    if (response && response.data && response.data.priorities) {
      const data: Priority[] = response.data.priorities;
      setAllPriorities(data);
    }
  }, [selectedOrganization]);

  useEffect(() => {
    fetchOrganizationPriorities();
  }, [fetchOrganizationPriorities]);

  useEffect(() => {
    if (
      selectedOrganization?.id &&
      allPriorities.length > 0 &&
      !selectedPriority
    ) {
      const defaultPriority = allPriorities[0];
      if (defaultPriority) {
        setSelectedPriority(defaultPriority);
      }
    }
  }, [selectedOrganization, allPriorities, selectedPriority]);

  const LAST_SELECTED_PROCESS_KEY = 'lastSelectedProcess';

  const saveLastSelectedProcess = (process: any) => {
    try {
      localStorage.setItem(LAST_SELECTED_PROCESS_KEY, JSON.stringify({
        id: process.id,
        name: process.name,
        processmodelname: process.processmodelname,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.warn('Errore nel salvare ultimo processo selezionato:', error);
    }
  };

  const getLastSelectedProcess = () => {
    try {
      const saved = localStorage.getItem(LAST_SELECTED_PROCESS_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.warn('Errore nel recuperare ultimo processo selezionato:', error);
      return null;
    }
  };

  useEffect(() => {
    if (selectedProcess) {
      saveLastSelectedProcess(selectedProcess);
    }
  }, [selectedProcess]);

  // Effetto per lo scroll automatico quando:
  // 1. Il dialogo è aperto
  // 2. I processi sono stati caricati
  // 3. C'è un processo selezionato
  useEffect(() => {
    if (open && processes.length > 0 && selectedProcess && itemRefs.current[selectedProcess.id]) {
      // Usa requestAnimationFrame per assicurarsi che il DOM sia aggiornato
      requestAnimationFrame(() => {
        // Secondo frame per maggiore sicurezza
        requestAnimationFrame(() => {
          scrollToSelectedProcess();
        });
      });
    }
  }, [open, processes.length, selectedProcess?.id, scrollToSelectedProcess]); // Dipende da open, processi caricati e ID del processo selezionato

  // Effetto specifico per quando il dialogo viene aperto
  useEffect(() => {
    if (open) {
      // Aspetta che il dialogo sia completamente renderizzato
      const timer = setTimeout(() => {
        scrollToSelectedProcess();
      }, 300); // Delay più lungo per essere sicuri

      return () => clearTimeout(timer);
    }
  }, [open, scrollToSelectedProcess]);

  useEffect(() => {
    if (processes.length === 0) {
      setSelectedProcess(null);
      return;
    }

    if (processes.length === 1) {
      const singleProcess = processes[0];
      if (!selectedProcess || selectedProcess.id !== singleProcess.id) {
        setSelectedProcess(singleProcess);
      }
      return;
    }

    if (!selectedProcess) {
      const lastSelected = getLastSelectedProcess();
      if (lastSelected) {
        const matchingProcess = processes.find(p => p.id === lastSelected.id);
        if (matchingProcess) {
          setSelectedProcess(matchingProcess);
        }
      }
    }
  }, [processes, selectedProcess]);

  const handleStartProcess = async ({
    name,
    files,
    meta,
    comment,
  }: {
    name: string;
    meta: { [key: string]: UserData };
    comment: string;
    files: File[];
  }) => {
    setLoading(true);
    try {
      const payload = {
        idprocessmodel: selectedProcess?.idprocessmodel,
        idorganization: selectedOrganization?.id,
        idevent: selectedProcess?.id,
        startdate: dayjs().utc().toISOString(),
        enddate: null,
        description: name,
        userdata: meta,
      };

      const { data } = await processesApi.post("", payload);
      const processId = data.process.id;
      const labelPromises = checked.map((label) =>
        tagsApi.post(
          `/${selectedOrganization?.id}/processes/${processId}/tags/${label.id}`
        )
      );
      await Promise.all(labelPromises);
      const {
        previousprocesstoken: { id: processTokenId },
      } = data;

      const processtokenObject = data.processtoken;

      if (comment) {
        await flownodesApi.post(`${processTokenId}/comments`, {
          comment,
        });
      }

      if (selectedPriority && selectedPriority.id && processtokenObject) {
        const idprocess = processtokenObject.idprocess;
        const requestData = {};
        await flownodesApi.post(
          `/${idprocess}/priorities/${selectedPriority.id}`,
          requestData
        );
      }

      if (files) {
        let hasError = false;
        for (let file of files) {
          const payload = {
            idorganization: selectedOrganization?.id,
            filename: file.name,
            cloudlink: "",
            idtype: 1,
            description: file.name,
            blob: await toBase64(file),
          };
          try {
            await flownodesApi.post(`${processTokenId}/attachments`, payload);
          } catch {
            hasError = true;
          } finally {
          }
        }
        if (!hasError && files.length > 0) {
          sendSnack({
            type: "success",
            message: getTranslation(
              translations,
              "generic.fileuploadedsuccessfully"
            ),
          });
        }
      }
    } finally {
      reset();
      setSelectedProcess(null);
      setSelectedPriority(null);
      setOpen(false);
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={Boolean(open)}
      maxWidth={"lg"}
      fullWidth
      fullScreen={isMobile}
      onClose={() => setOpen(false)}
      PaperProps={{
        sx: {
          height: isMobile ? '100%' : '70vh', // Altezza ridotta al 70% dello schermo
          maxHeight: isMobile ? '100%' : '600px', // Altezza massima di 600px
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">
              {getTranslation(translations, "pm.startprocess.label.caption")}
            </Typography>
            <Labels labelCompleteMode labels={checked} />
          </Stack>
          <IconButton onClick={() => setOpen(false)}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          flex: 1,
          overflow: 'hidden', // Preveniamo overflow del contenuto
          display: 'flex',
          flexDirection: 'column',
          py: 2
        }}
      >
        {processes.length === 0 ? (
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            spacing={2}
          >
            <Box
              component="img"
              alt="..."
              sx={{
                width: "308px",
                height: "308px",
              }}
              src={noModel}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                color="textSecondary"
                component="p"
                sx={{ mt: 1, fontSize: "1.2rem", fontWeight: "bold" }}
              >
                {getTranslation(
                  translations,
                  "generic.create_one_model.caption"
                )}
              </Typography>
              {isAdmin && (
                <Button
                  onClick={() => navigate("/dashboard/process/insert")}
                  variant="outlined"
                  size="large"
                  sx={{ mt: 3 }}
                >
                  {getTranslation(translations, "pm.label.button.process_name")}
                </Button>
              )}
            </Box>
          </Stack>
        ) : (
          <Grid container spacing={2} sx={{ height: '100%', overflow: 'hidden' }}>
            {/* Colonna sinistra: Lista processi con ricerca e ordinamento */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Stack spacing={2} sx={{ height: '100%' }}>
                {/* Barra con ricerca e ordinamento */}
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder={getTranslation(translations, "pm.search.model")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {filteredProcesses.length > 0 && (
                    <IconButton
                      onClick={
                        sortOrder === SortState.Asc
                          ? sortProcessesDesc
                          : sortProcessesAsc
                      }
                      size="small"
                    >
                      {sortOrder === SortState.Asc ? (
                        <ArrowDownward />
                      ) : (
                        <ArrowUpward />
                      )}
                    </IconButton>
                  )}
                </Stack>

                {/* Lista dei processi */}
                <List
                  ref={listRef}
                  id="process-list"
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    padding: 1,
                    flex: 1,
                    minHeight: 0, // Importante per permettere al flex di funzionare
                    maxHeight: isMobile ? 250 : 350, // Riduco l'altezza massima
                    overflow: "auto",
                    backgroundColor: 'background.paper',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'rgba(0,0,0,0.05)',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(0,0,0,0.2)',
                      borderRadius: '4px',
                    }
                  }}
                >
                  {filteredProcesses.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ p: 2, textAlign: 'center' }}
                    >
                      {searchQuery
                        ? (getTranslation(translations, "pm.no.processes.found") || "Nessun processo trovato")
                        : (getTranslation(translations, "pm.no.processes.available") || "Nessun processo disponibile")
                      }
                    </Typography>
                  ) : (
                    filteredProcesses.map((process) => (
                      <ListItemButton
                        ref={(el) => itemRefs.current[process.id] = el}
                        key={process.id}
                        id={`startprocess-fields-${process.id}`}
                        selected={selectedProcess?.id === process.id}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.light',
                            '&:hover': {
                              backgroundColor: 'primary.light',
                            }
                          }
                        }}
                        onClick={() => {
                          if (selectedProcess?.id !== process.id) {
                            setSelectedProcess(process);
                            saveLastSelectedProcess(process);
                          }
                        }}
                      >
                        <Typography variant="body2" noWrap>
                          {process.name === ""
                            ? process.processmodelname
                            : `${process.processmodelname} (${process.name})`}
                        </Typography>
                      </ListItemButton>
                    ))
                  )}
                </List>
              </Stack>
            </Grid>

            {/* Colonna centrale: Dettagli processo */}
            <Grid item xs={12} md={4} sx={{ height: '100%', overflow: 'auto' }}>
              <Stack spacing={2}>
                <Typography variant="h6">
                  {getTranslation(translations, "pm.process.details")}
                </Typography>

                <TextField
                  id="startprocess-name"
                  fullWidth
                  inputProps={{ maxLength: 7000 }}
                  variant="outlined"
                  size="small"
                  label={getTranslation(
                    translations,
                    "pm.processstart.name.label"
                  )}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                  value={values.name}
                  onChange={({ target }) =>
                    changeHandler("name", target.value)
                  }
                />

                <FlowentiButton
                  size="small"
                  variant="outlined"
                  component="label"
                  onClick={(e) => setIsLabelManagerShown(e.currentTarget)}
                  fullWidth
                >
                  {getTranslation(translations, "generic.labels.caption")}
                </FlowentiButton>

                {isLabelManagerShown && (
                  <LabelManager
                    checked={checked}
                    setChecked={setChecked}
                    open={isLabelManagerShown}
                    handleClose={handleLabelManagerClose}
                  />
                )}

                <PrioritiesDropDown
                  allPriorities={allPriorities}
                  value={selectedPriority}
                  onChange={(newPriority) =>
                    setSelectedPriority(newPriority)
                  }
                  administrator={administrator}
                />

                <Box>
                  <Typography variant="h6" mt={4} mb={2}>
                    {getTranslation(translations, "settings.attachments.label")}
                  </Typography>
                  <DropZone
                    files={selectedFiles}
                    onFileChange={handleFileChange}
                    maxFiles={10}
                  />
                </Box>
              </Stack>
            </Grid>

            {/* Colonna destra: Commenti e campi personalizzati */}
            <Grid item xs={12} md={4} sx={{ height: '100%', overflow: 'auto' }}>
              <Stack spacing={2} sx={{ height: '100%' }}>
                <Box>
                  <Typography variant="h6">
                    {getTranslation(translations, "settings.comments.label")}
                  </Typography>
                  <Box sx={{ maxHeight: '150px', overflow: 'auto', p: 2 }}>
                    <InputComment
                      shadow
                      onChange={(c) => changeHandler("comment", c)}
                      value={values.comment || ""}
                    />
                  </Box>
                </Box>

                <FlowentiButton
                  id="addfield-button"
                  startIcon={<AddCircleOutlineRounded />}
                  size="small"
                  variant="outlined"
                  onClick={() => setOpenAddFieldDialog(true)}
                >
                  {getTranslation(translations, "pm.mayactivities.addfield.label")}
                </FlowentiButton>

                {selectedProcess?.userschema && (
                  <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                    <FieldsList
                      linkChange
                      enableUndefined
                      handleSaveUserdata={async (newValues) =>
                        changeHandler("meta", newValues)
                      }
                      selectedProcess={selectedProcess?.id}
                      schema={selectedProcess?.userschema}
                      openAddFieldDialog={openAddFieldDialog}
                      onAddFieldDialogClose={() => setOpenAddFieldDialog(false)}
                      value={values.meta}
                      onChange={(newValues) => changeHandler("meta", newValues)}
                    />
                  </Box>
                )}
              </Stack>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      {processes.length > 0 && (
        <DialogActions sx={{ paddingRight: "19px" }}>
          <Stack direction="row" spacing={2}>
            <Button
              id="cancel-button-process"
              color="primary"
              variant="outlined"
              size="small"
              onClick={() => setOpen(false)}
              sx={{
                color: "black",
                borderColor: (_) => _.palette.grey[200],
              }}
            >
              {getTranslation(
                translations,
                "pm.startprocess.button.cancel.caption"
              )}
            </Button>
            <FlowentiButton
              id="startprocess-button2"
              variant="contained"
              color="primary"
              size="small"
              disabled={!isValid || !selectedProcess || loading}
              onClick={() =>
                handleStartProcess({ ...values, files: selectedFiles })
              }
            >
              {getTranslation(
                translations,
                "pm.startprocess.button.ok.caption"
              )}
            </FlowentiButton>
          </Stack>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default StartProcess;